import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FiActivity, FiUpload, FiPackage, FiAlertCircle, FiServer } from 'react-icons/fi';
import { getNpdHealth, NPD_BASE_URL } from '../../utils/npdApi';

const NPDDashboard = ({ onNavigate }) => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    getNpdHealth()
      .then(setHealth)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="npd-loading">
        <div className="npd-spinner" />
        <span>Checking NPD API health...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="npd-error">
        <FiAlertCircle size={28} />
        <span>{error}</span>
        <button onClick={load}>Retry</button>
      </div>
    );
  }

  const azure = health?.azure || {};
  const stats = [
    { label: 'API Status', value: health?.status || 'unknown', icon: <FiActivity size={20} />, badge: health?.status === 'ok' ? 'status-completed' : 'status-failed' },
    { label: 'Endpoint', value: NPD_BASE_URL, icon: <FiServer size={20} /> },
    { label: 'Azure Deployment', value: azure.deployment || 'n/a', icon: <FiPackage size={20} /> },
    { label: 'Ready To Use', value: 'Upload PDF', icon: <FiUpload size={20} /> },
  ];

  return (
    <div className="npd-dashboard">
      <motion.div
        className="npd-stats-grid"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        {stats.map((s) => (
          <motion.div
            key={s.label}
            className="npd-stat-card"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="npd-stat-icon">{s.icon}</div>
            <div className="npd-stat-info">
              <span className="npd-stat-value" style={{ fontSize: 16, lineHeight: 1.3 }}>{s.value}</span>
              <span className="npd-stat-label">{s.label}</span>
            </div>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        className="npd-card"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.3 }}
      >
        <div className="npd-card-header">
          <div className="npd-card-title">
            <FiServer size={16} />
            <span>Health Details</span>
          </div>
          <button className="npd-link-btn" onClick={() => onNavigate('upload')}>
            <FiUpload size={13} /> Open Upload
          </button>
        </div>

        <div className="npd-table-wrap">
          <table className="npd-table">
            <tbody>
              <tr>
                <td className="npd-mono">status</td>
                <td><span className={`npd-badge ${health?.status === 'ok' ? 'status-completed' : 'status-failed'}`}>{health?.status || 'unknown'}</span></td>
              </tr>
              <tr>
                <td className="npd-mono">azure.endpoint</td>
                <td className="npd-mono">{azure.endpoint || 'NOT SET'}</td>
              </tr>
              <tr>
                <td className="npd-mono">azure.deployment</td>
                <td className="npd-mono">{azure.deployment || 'gpt-4o'}</td>
              </tr>
              <tr>
                <td className="npd-mono">azure.api_version</td>
                <td className="npd-mono">{azure.api_version || '2024-02-15-preview'}</td>
              </tr>
              <tr>
                <td className="npd-mono">azure.key_set</td>
                <td>
                  <span className={`npd-badge ${azure.key_set ? 'status-completed' : 'status-failed'}`}>
                    {String(Boolean(azure.key_set))}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default NPDDashboard;
